import { Badge, Card, Form } from "react-bootstrap";
import {
  CATEGORIES,
  FREE_TEXT_PROMPTS,
  RECOMMENDATIONS,
  deriveCategoryScores,
  type ScorecardInput,
  weightedOverall,
} from "@ld-scorecard/shared";
import ScoreSelector from "./ScoreSelector";

type Draft = ScorecardInput;

export default function ScorecardForm({
  draft,
  setDraft,
  disabled,
}: {
  draft: Draft;
  setDraft: React.Dispatch<React.SetStateAction<Draft>>;
  disabled?: boolean;
}) {
  const setSignal = (catId: string, sigId: string, v: number) =>
    setDraft((d) => ({
      ...d,
      signalScores: {
        ...d.signalScores,
        [catId]: { ...(d.signalScores[catId] ?? {}), [sigId]: v },
      },
    }));

  const setNote = (catId: string, text: string) =>
    setDraft((d) => ({ ...d, categoryNotes: { ...d.categoryNotes, [catId]: text } }));

  const toggleFlag = (catId: string, flagId: string) =>
    setDraft((d) => {
      const current = d.redFlags[catId] ?? [];
      const next = current.includes(flagId)
        ? current.filter((f) => f !== flagId)
        : [...current, flagId];
      return { ...d, redFlags: { ...d.redFlags, [catId]: next } };
    });

  const derived = deriveCategoryScores(draft.signalScores);
  const live = weightedOverall({ categoryScores: derived });

  return (
    <>
      {CATEGORIES.map((cat) => (
        <Card key={cat.id} className="mb-3 shadow-sm">
          <Card.Header className="category-header d-flex justify-content-between align-items-center">
            <span className="fw-semibold">
              {cat.ordinal}. {cat.title}
            </span>
            <Badge bg="light" text="dark">
              Weight {cat.weight}%
            </Badge>
          </Card.Header>
          <Card.Body>
            {cat.note && <p className="text-muted fst-italic small">{cat.note}</p>}
            <div className="mb-3">
              {cat.signals.map((sig) => (
                <div
                  key={sig.id}
                  className="signal-row d-flex flex-column flex-sm-row align-items-start align-items-sm-center justify-content-between gap-2 py-2 border-bottom"
                >
                  <div className="pe-sm-3 flex-grow-1">{sig.text}</div>
                  <ScoreSelector
                    name={`${cat.id}-${sig.id}`}
                    value={draft.signalScores[cat.id]?.[sig.id]}
                    onChange={(v) => setSignal(cat.id, sig.id, v)}
                    disabled={disabled}
                  />
                </div>
              ))}
            </div>

            {cat.redFlags && cat.redFlags.length > 0 && (
              <div className="mb-3">
                <div className="small text-danger fw-semibold mb-1">Red flags observed</div>
                <div className="d-flex flex-wrap gap-3">
                  {cat.redFlags.map((flag) => (
                    <Form.Check
                      key={flag.id}
                      type="checkbox"
                      id={`flag-${cat.id}-${flag.id}`}
                      label={flag.text}
                      checked={(draft.redFlags[cat.id] ?? []).includes(flag.id)}
                      onChange={() => toggleFlag(cat.id, flag.id)}
                      disabled={disabled}
                    />
                  ))}
                </div>
              </div>
            )}

            <Form.Group className="mb-3">
              <Form.Label className="small text-muted">Notes</Form.Label>
              <Form.Control
                as="textarea"
                className="notes"
                value={draft.categoryNotes[cat.id] ?? ""}
                onChange={(e) => setNote(cat.id, e.target.value)}
                disabled={disabled}
              />
            </Form.Group>

            <div className="d-flex align-items-center gap-2">
              <span className="fw-semibold">Category score (1–5):</span>
              <Badge bg="dark" className="fs-6 score-pill">
                {derived[cat.id] ?? "—"}
              </Badge>
              <span className="text-muted small">average of the signals above</span>
            </div>
          </Card.Body>
        </Card>
      ))}

      <Card className="mb-3 shadow-sm">
        <Card.Header className="fw-semibold">Overall</Card.Header>
        <Card.Body>
          <div className="mb-3">
            <span className="fw-semibold">Weighted overall (live): </span>
            <Badge bg="dark" className="fs-6">
              {live === null ? "—" : `${live} / 5`}
            </Badge>
            <span className="text-muted small ms-2">
              category scores are the signal averages, weighted by category
            </span>
          </div>

          <Form.Group className="mb-3">
            <Form.Label className="fw-semibold">Overall recommendation</Form.Label>
            <div className="d-flex flex-wrap gap-3">
              {RECOMMENDATIONS.map((r) => (
                <Form.Check
                  key={r.value}
                  type="radio"
                  name="recommendation"
                  id={`rec-${r.value}`}
                  label={r.label}
                  checked={draft.recommendation === r.value}
                  onChange={() => setDraft((d) => ({ ...d, recommendation: r.value }))}
                  disabled={disabled}
                />
              ))}
            </div>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label className="fw-semibold">{FREE_TEXT_PROMPTS.coachEveryone}</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={draft.coachEveryone ?? ""}
              onChange={(e) => setDraft((d) => ({ ...d, coachEveryone: e.target.value }))}
              disabled={disabled}
            />
          </Form.Group>

          <Form.Group>
            <Form.Label className="fw-semibold">{FREE_TEXT_PROMPTS.gapConcern}</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={draft.gapConcern ?? ""}
              onChange={(e) => setDraft((d) => ({ ...d, gapConcern: e.target.value }))}
              disabled={disabled}
            />
          </Form.Group>
        </Card.Body>
      </Card>
    </>
  );
}
